import { Application, Container } from "pixi.js";

import type { IStudioArea } from "../../../screens/Dashboard/SubComponents/Studio/interfaces/sub-interfaces/Areas.interface";

import { EStudioOverlayType } from "../../../screens/Dashboard/SubComponents/Studio/interfaces/sub-interfaces/Overlay.interface";

import DrawCache from "./DrawCache";

import StrategyBounds from "./StrategyBounds";

import CustomOverlayRenderer from "./Overlays/CustomOverlayRenderer";

import {
  UpdaterList,
  type OverlayFrameUpdater,
} from "./Overlays/OverlayElementAnimator";

class OverlaysFactory {
  private Root: Container;

  private DrawCache: DrawCache;

  private customRenderer: CustomOverlayRenderer;

  // Updaters per-frame (animaciones, partículas, procedural, typewriter) de
  // todas las áreas + reloj. Lista VIVA: las registraciones tardías caen acá y
  // `OnUpdater` le avisa al ticker de RenderFactory, que los corre por frame.

  public Anim: {
    updaters: OverlayFrameUpdater[];

    startMs: number;
  } | null = null;

  private OnUpdater: (() => void) | undefined;

  private Options = {
    width: 1920,

    height: 1080,
  };

  constructor(
    _app: Application,
    drawCache: DrawCache,
    root: Container,
    onUpdater?: () => void,
  ) {
    this.Root = root;

    this.DrawCache = drawCache;

    this.customRenderer = new CustomOverlayRenderer();

    this.OnUpdater = onUpdater;
  }

  public async renderOverlays(areas: IStudioArea[]) {
    this.Anim = null;

    if (!areas || areas.length === 0) {
      return;
    }

    // Reinicia el reloj en cada rebuild (las entry-anims se reproducen de nuevo).
    const updaters = new UpdaterList();

    updaters.onPush = this.OnUpdater;

    this.Anim = { updaters, startMs: Date.now() };

    for (const area of areas) {
      const overlay = area.overlay;

      if (!overlay || !overlay.active) continue;
      if (overlay.type !== EStudioOverlayType.CUSTOM) continue;

      const overlayKey = `overlay_${area.id}`;

      const sizeArea = StrategyBounds.getBounds({
        bound: area.bounds,

        container: {
          width: this.Options.width,

          height: this.Options.height,
        },
      });

      let overlayContainer = this.DrawCache.Containers[overlayKey];

      const reused = !!overlayContainer && !overlayContainer.destroyed;

      if (!overlayContainer || overlayContainer.destroyed) {
        overlayContainer = new Container();

        overlayContainer.label = overlayKey;

        this.DrawCache.Containers[overlayKey] = overlayContainer;

        this.Root.addChild(overlayContainer);
      } else {
        for (const child of overlayContainer.removeChildren()) {
          child.destroy({ children: true });
        }

        overlayContainer.mask = null;
      }

      overlayContainer.position.set(sizeArea.left, sizeArea.top);

      // Sit on top of the area video using a fractional bump; Pixi sorts numerically
      overlayContainer.zIndex = (area.bounds.zIndex ?? 0) + 0.5;

      void reused;

      this.customRenderer.render(
        overlay,
        overlayContainer,
        { width: sizeArea.width, height: sizeArea.height },
        undefined,
        updaters,
      );

      this.Root.sortDirty = true;
    }
  }

  public destroy() {
    this.Anim = null;

    for (const key in this.DrawCache.Containers) {
      if (key.startsWith("overlay_")) {
        const container = this.DrawCache.Containers[key];

        if (container && !container.destroyed) {
          try {
            container.destroy({ children: true });
          } catch (e) {
            console.warn("Error destroying overlay container:", e);
          }
        }

        delete this.DrawCache.Containers[key];
      }
    }
  }
}

export default OverlaysFactory;
